/**
 * Plan tier (Starter / Pro) and the Pro "included channels" allowance.
 *
 * Reads through to live Stripe billing via `useBillingStatus()` — the plan,
 * the social allowance, and which channels currently sit in the `_included`
 * price slot. This is a read-only derivation hook: channel enable/disable and
 * the matching billing writes live in `useChannelActions`, which is the single
 * source of truth for billing mutations (so a toggle never double-POSTs).
 *
 * For local dev / the Design Guidelines preview page, a localStorage override
 * (`unify.tier.override`) lets you flip the displayed tier without touching
 * Stripe. The override only takes effect when there is no resolvable
 * subscription (no plan returned by `useBillingStatus()`).
 */
import { useEffect, useMemo, useState } from 'react';

import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useShop } from '@/hooks/useShop';

export const TIERS = ['starter', 'pro'];

export const PRO_INCLUDED_LIMIT = 2;

const TIER_KEY = 'unify.tier.override';
const TIER_EVENT = 'tier:change';

const readOverride = () => {
	if (typeof window === 'undefined') return null;
	const value = window.localStorage.getItem(TIER_KEY);
	return TIERS.includes(value) ? value : null;
};

export function useTier() {
	const { shop } = useShop();
	const { billing } = useBillingStatus(shop?.id);
	const [override, setOverrideState] = useState(readOverride);

	useEffect(() => {
		const sync = () => setOverrideState(readOverride());
		window.addEventListener('storage', sync);
		window.addEventListener(TIER_EVENT, sync);
		return () => {
			window.removeEventListener('storage', sync);
			window.removeEventListener(TIER_EVENT, sync);
		};
	}, []);

	// Real plan from Stripe takes precedence. When the customer has no
	// subscription yet (no_subscription / loading), fall back to the dev
	// override and ultimately to Pro so the design renders with paid channels
	// visible as "free with Pro".
	const tier = billing?.plan ?? override ?? 'pro';

	const setTier = (next) => {
		if (!TIERS.includes(next)) return;
		window.localStorage.setItem(TIER_KEY, next);
		setOverrideState(next);
		window.dispatchEvent(new Event(TIER_EVENT));
	};

	const includedSlugs = useMemo(() => {
		const channels = billing?.entitlements?.channels;
		if (!channels) return [];
		return Object.entries(channels)
			.filter(([, c]) => c.priceVariant === 'included')
			.map(([id]) => id);
	}, [billing?.entitlements?.channels]);

	const slotsLimit = billing?.entitlements?.socialAllowance?.limit ?? PRO_INCLUDED_LIMIT;
	const slotsUsed = billing?.entitlements?.socialAllowance?.used ?? includedSlugs.length;
	const slotsAvailable = Math.max(0, slotsLimit - slotsUsed);

	const isIncluded = (id) => includedSlugs.includes(id);

	return {
		tier,
		isPro: tier === 'pro',
		setTier,
		includedSlugs,
		slotsUsed,
		slotsLimit,
		slotsAvailable,
		isIncluded
	};
}
